(ns app.front.view.public-records.core
  (:require [app.domain.public-map.model :as public-map-model]
            [app.front.ui :as ui]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.section :as section]
            [app.front.view.create-application.gis-search :as gis-search]
            [app.front.view.landing-page :as landing-page]
            [app.front.view.not-found :as not-found]
            [app.front.view.public-records.applications :as pr-applications]
            [app.front.view.public-records.common :as pr-common]
            [app.front.view.public-records.complaints :as pr-complaints]
            [app.front.view.public-records.contractor-registrations :as pr-contractor-registrations]
            [app.front.view.public-records.licences :as pr-licences]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))

;; ---- Events: init & tab orchestration ---------------------------------------

(def ^:private include-secondary-map-records-default false)

(defn- current-tab [{:keys [tabs selected-tab]}]
  (pr-common/tab-idx->tab tabs selected-tab))

(defn- selected-property-for-current-tab [db]
  (case (current-tab db)
    :public-records/complaints (:complaint-selected-property db)
    (:public-records/development
      :public-records/periodic-inspections
      :public-records/cases
      :public-records/work-orders)
    (:selected-property db)
    (or (:selected-property db)
        (:complaint-selected-property db))))

(defn- combined-map-available? [tabs]
  (and (some #{:public-records/cases} tabs)
       (some #{:public-records/complaints} tabs)))

(def ^:private secondary-map-keys
  [:secondary-complaint-search-results
   :secondary-complaint-total-count
   :secondary-complaint-selected-result
   :secondary-application-search-results
   :secondary-application-total-count
   :secondary-application-selected-result])

(defn- clear-secondary-map-state [db]
  (apply dissoc db secondary-map-keys))

(defn- not-found-state [branding]
  {:branding     branding
   :config       nil
   :tabs         []
   :selected-tab 0})

(defn- page-header [tr match]
  [ui/page-header (util/mmap match)
   [:div
    [:h1.bold (tr [:public-records/public-records])]]])

(defn- info-text-view [lang info-text]
  [:div.max-w-none.text-xs.md:text-base.p-0.sm:p-4
   {:key (str "public-records-info-" lang)}
   [ui-rt/editor {:id                 "public-records-info-text"
                  :initial-html-value info-text}]])

(defn- superdomain-branding? [db branding]
  (and (= branding (:branding db))
       (not (:branding/loading? db))
       (= :group.type/superdomain (:branding/group-type db))))

(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ branding tab-query-param]]
    (cond-> {:db (assoc db ::pr-common/data (not-found-state branding))}
      (not (superdomain-branding? db branding))
      (assoc :fx [[:dispatch [:comm/command-monitored
                              {:command      :public-records/config
                               :method       :get
                               :data         (util/mmap branding)
                               :on-success   [::init.success branding tab-query-param]
                               :on-not-found [::init.not-found branding]}]]]))))

(rf/reg-event-db ::init.not-found
  pr-common/data-path
  (fn [_ [_ branding & _]]
    (not-found-state branding)))

(rf/reg-event-fx ::init.success
  pr-common/data-path
  (fn [{:keys [db]} [_ branding tab-query-param _status response]]
    (let [resolved-branding (or (some-> response :domain-group :group/uri-path-id)
                                branding)
          resolved-tab      (when (seq tab-query-param) (keyword "public-records" tab-query-param))
          tabs              (->> (or (:public-records-tabs response)
                                     (some-> response
                                             :permit-types
                                             public-map-model/public-records-tabs))
                                 (sort-by pr-common/tab-order))
          selected-tab      (if resolved-tab
                              (or (p/find-index tabs (partial = resolved-tab)) 0)
                              0)
          first-tab         (nth tabs selected-tab nil)
          tab-permit-types  (pr-common/available-tab-permit-types (:permit-types response) first-tab)]
      {:db (-> db
               clear-secondary-map-state
               (assoc :branding resolved-branding
                      :config response
                      :selected-tab selected-tab
                      :tabs tabs
                      :current-application-tab first-tab
                      :tab-permit-types tab-permit-types
                      :visible-filters nil
                      :selected-view :dashboard-search/list-view
                      :include-secondary-map-records? include-secondary-map-records-default
                      :skip 0
                      :limit 50
                      :application-loading-more? false
                      :sort-condition (pr-common/default-sort-for-tab first-tab)
                      :total-count 0
                      :complaint-visible-filters nil
                      :complaint-selected-view :dashboard-search/list-view
                      :complaint-skip 0
                      :complaint-limit 50
                      :complaint-loading-more? false
                      :complaint-sort-condition [:created :desc]
                      :complaint-total-count 0))
       :fx (cond-> []
             (seq tabs)
             (conj [:dispatch [::pr-common/load-public-map-layers
                               (-> response :domain-group :group/id)]])

             (pr-common/application-tabs first-tab)
             (conj [:dispatch [::pr-applications/search]])

             (= first-tab :public-records/complaints)
             (conj [:dispatch [::pr-complaints/search]])

             (= first-tab :public-records/licences)
             (conj [:dispatch [::pr-licences/ensure-init resolved-branding]])

             (= first-tab :public-records/contractor-registrations)
             (conj [:dispatch [::pr-contractor-registrations/ensure-init resolved-branding]]))})))

(rf/reg-event-fx ::change-tab
  pr-common/data-path
  (fn [{:keys [db]} [_ tab-idx]]
    (let [tab               (pr-common/tab-idx->tab (:tabs db) tab-idx)
          selected-property (selected-property-for-current-tab db)
          branding          (or (some-> db :config :domain-group :group/uri-path-id)
                                (:branding db))
          tab-permit-types  (pr-common/available-tab-permit-types (:permit-types (:config db)) tab)]
      {:db (cond-> (-> db
                       (assoc :selected-tab tab-idx)
                       clear-secondary-map-state
                       (assoc :include-secondary-map-records? include-secondary-map-records-default
                              :selected-property selected-property
                              :complaint-selected-property selected-property))
             (pr-common/application-tabs tab)
             (assoc :current-application-tab tab
                    :tab-permit-types tab-permit-types
                    :visible-filters nil
                    :skip 0
                    :application-loading-more? false
                    :selected-options {}
                    :application-search-results []
                    :total-count 0
                    :sort-condition (pr-common/default-sort-for-tab tab)
                    :selected-result nil
                    :selected-cluster-results nil)

             (= tab :public-records/complaints)
             (assoc :complaint-skip 0
                    :complaint-loading-more? false
                    :complaint-selected-result nil
                    :complaint-selected-cluster-results nil))
       :fx (cond-> (cond
                     (pr-common/application-tabs tab)
                     [[:dispatch [::pr-applications/search {:keep-results? false}]]]

                     (= tab :public-records/complaints)
                     [[:dispatch [::pr-complaints/search {:keep-results? false}]]]

                     (= tab :public-records/licences)
                     [[:dispatch [::pr-licences/ensure-init branding]]]

                     (= tab :public-records/contractor-registrations)
                     [[:dispatch [::pr-contractor-registrations/ensure-init branding]]]

                     :else [])
             (nil? selected-property)
             (conj [:dispatch [::gis-search/clear-address-search]]))})))

;; ---- Orchestration subscriptions --------------------------------------------

(rf/reg-sub ::tabs
  :<- [::pr-common/data]
  :-> :tabs)

(rf/reg-sub ::info-text
  :<- [::pr-common/data]
  :<- [:language]
  (fn [[{:keys [config]} lang] _]
    (let [localized (:group/public-map-info-text config)]
      (or (get localized lang)
          (get localized :en)
          (some val localized)
          (:group/info-text-for-public-map config)))))

(rf/reg-sub ::selected-tab
  :<- [::pr-common/data]
  (fn [{:keys [selected-tab]}]
    (or selected-tab 0)))

(rf/reg-sub ::config
  :<- [::pr-common/data]
  :-> :config)

(rf/reg-event-fx ::set-include-secondary-map-records
  pr-common/data-path
  (fn [{:keys [db]} [_ include-secondary?]]
    (when (combined-map-available? (:tabs db))
      (let [search-evt (case (current-tab db)
                         :public-records/cases
                         ::pr-applications/search-secondary-complaints-for-map

                         :public-records/complaints
                         ::pr-complaints/search-secondary-applications-for-map

                         nil)]
        (cond-> {:db (cond-> (-> (assoc db :include-secondary-map-records? include-secondary?)
                                 (dissoc :selected-cluster-results :complaint-selected-cluster-results))
                       (not include-secondary?)
                       clear-secondary-map-state)}
          (and include-secondary? search-evt)
          (assoc :fx [[:dispatch [search-evt]]]))))))

(rf/reg-sub ::include-secondary-map-records?
  :<- [::pr-common/data]
  (fn [db]
    (boolean
      (and (combined-map-available? (:tabs db))
           (:include-secondary-map-records? db)))))

(defn- combined-tab-view [view-component]
  (let [include-secondary-map-records? (<sub [::include-secondary-map-records?])
        combined-map-available?        (combined-map-available? (<sub [::tabs]))]
    [view-component {:include-secondary-map-records?  include-secondary-map-records?
                     :combined-map-available?         combined-map-available?
                     :on-toggle-secondary-map-records #(>evt [::set-include-secondary-map-records %])}]))

(rf/reg-sub ::tab-sections
  :<- [::tabs]
  :<- [:tr]
  (fn [[tabs tr] _]
    (mapv (fn [tab]
            {:label-text (tr [tab])
             :content    (cond
                           (= tab :public-records/cases)
                           [combined-tab-view pr-applications/view]

                           (pr-common/application-tabs tab)
                           [pr-applications/view]

                           (= tab :public-records/complaints)
                           [combined-tab-view pr-complaints/view]

                           (= tab :public-records/licences)
                           [pr-licences/tab-view]

                           (= tab :public-records/contractor-registrations)
                           [pr-contractor-registrations/tab-view]

                           :else
                           [:<>])})
          tabs)))

;; ---- Page view --------------------------------------------------------------

(defn- view [match]
  (let [tr        (<sub [:tr])
        lang      (<sub [:language])
        loading?  (<sub [:comm/command-processing? :public-records/config])
        config    (<sub [::config])
        tabs      (<sub [::tab-sections])
        info-text (<sub [::info-text])]
    (cond
      loading?
      [:div.cloudpermit-page
       [section/section-skeleton]]

      (nil? config)
      [not-found/not-found-view]

      (empty? tabs)
      [:div.cloudpermit-page
       [page-header tr match]
       (when-not (util/nil-or-blank? info-text)
         [info-text-view lang info-text])
       [:div.bg-white.border.border-solid.border-ui-dark-gray.p-6.sm:p-8.text-center
        [:p.mb-0 (tr [:public-records/no-public-records-description])]]]

      :else
      [:div.cloudpermit-page
       [page-header tr match]
       (when-not (util/nil-or-blank? info-text)
         [info-text-view lang info-text])
       [section/tabbed-section
        {:selected-tab-index (<sub [::selected-tab])
         :on-change          #(>evt [::change-tab %])
         :tabs               tabs
         :header-extra       [pr-common/contact-request-button {:tr         tr
                                                                :kind       :secondary
                                                                :class-name "mb-2"}]}]])))

(rf/reg-sub ::view-data
  :<- [:session/logged-in?]
  (fn [logged-in? _]
    {:back-href (href/href (if logged-in?
                             :dashboard
                             :view/landing-page))}))

(def route
  {:name          :view/public-records
   :view          view
   :view-sub      [::view-data]
   :public-header landing-page/landing-header
   :public?       true
   :parameters    {:path  {:branding s/Str}
                   :query {(s/optional-key :tab) s/Str}}
   :controllers   [{:parameters {:path  [:branding]
                                 :query [:tab]}
                    :start      (fn [{{:keys [branding]} :path
                                      {:keys [tab]}      :query}]
                                  (>evt [::init branding tab])
                                  (>evt [::landing-page/init branding]))}]})
